import React, { FC, ReactElement, useState } from 'react'
import Error from '../../../Components/UI/Error'
import Load from '../../../Components/UI/Load'
import { getStatuses } from '../../../Graphql-Utilities'
import { useBookingEventQuery } from './Booking.generated-types'
import { ConfirmedBooking } from './ConfirmedBooking'
import { ContactAmModal, ContactAmModalVariant } from './ContactAmModal'
import { EmptyState } from './EmptyState'
import { InProgressBooking } from './InProgressBooking'
import { NoAcceptedProposal } from './NoAcceptedProposal'
import { s } from './style'
import { WithdrawBrief } from './WithdrawBrief'

interface Props {
  selectedEventUUID?: string
  hasNoEvents: boolean
  eventsLoading: boolean
  onCancelEventSuccess: () => void
}

export const Booking: FC<Props> = ({
  selectedEventUUID,
  hasNoEvents,
  eventsLoading,
  onCancelEventSuccess,
}): ReactElement => {
  const [{ fetching, error, data }, refetchEvent] = useBookingEventQuery({
    variables: { UUID: selectedEventUUID },
    pause: !selectedEventUUID || hasNoEvents,
    requestPolicy: 'cache-and-network',
  })

  const [isModalVisible, setIsModalVisible] = useState(false)

  const handleCloseContactAmModal = (): void => setIsModalVisible(false)

  if (!eventsLoading && error) {
    return <Error error={error}></Error>
  }

  if (hasNoEvents) {
    return <EmptyState />
  }

  if (eventsLoading || (fetching && !data)) {
    return <Load load={true}></Load>
  }

  if (!data) {
    return null
  }

  const event = data == null ? null : data.getEventByUUID

  // If a proposal is already booked, we don't want to be selecting other proposals
  const requestedProposal =
    data == null ? null : event.proposals.find((p) => p.status === 'RequestedToBook')

  const { isBookingConfirmed, isRequestedToBook } = getStatuses(event)

  if ((isRequestedToBook || isBookingConfirmed) && !requestedProposal) {
    return <NoAcceptedProposal />
  }

  return (
    <div style={s.root}>
      {isBookingConfirmed ? (
        <ConfirmedBooking event={event} proposal={requestedProposal} />
      ) : (
        <>
          <ContactAmModal
            variant={ContactAmModalVariant.Cancel}
            visible={isModalVisible}
            handleClose={handleCloseContactAmModal}
          />
          <InProgressBooking
            event={event}
            requestedProposal={requestedProposal}
            refetchEvent={refetchEvent}
          />
          <WithdrawBrief event={event} onCancelSuccess={onCancelEventSuccess} />
        </>
      )}
    </div>
  )
}
